import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import {
  CartesianGrid,
  ResponsiveContainer,
  ScatterChart as RechartScatterChart,
  XAxis,
  YAxis,
  Tooltip as RechartTooltip,
  Scatter,
} from 'recharts';
import { ScatterPointItem } from 'recharts/types/cartesian/Scatter';
import { theme } from '@frontend/theme';
import { EmptyData, renderHorizontalMarker, Tooltip } from '../atoms';
import { useChartContext } from '../chart.provider';
import { EMPTY_STATE_MIN_HEIGHT } from '../constants';
import { defaultFormatter, showEmptyStateForScatterChart } from '../helpers';
import { calcPlotRadius } from './calc-plot-radius';
import { Appearance, ScatterChartData, Value } from './types';

type Props = {
  appearance?: Appearance;
  data?: ScatterChartData;
};

const defaultMargin = { top: 20, right: 40, bottom: 8, left: 0 };

const axisLine = {
  stroke: theme.colors.neutral10,
  strokeDasharray: '1 1',
  strokeWidth: 1,
};

export const ScatterChart = memo(({ appearance = {}, data = { groups: [] } }: Props) => {
  const {
    customTooltipTitle,
    customXAxisTick,
    customXAxisTickFormat,
    customYAxisTick,
    customYAxisTickFormat,
    fixSize,
    height = 240,
    hideHoveringCursor,
    hideMarkerEnds,
    hideTooltip,
    margin,
    maxRadius,
    minRadius,
    showGridLines,
    showXAxis,
    showYAxis,
    tooltipLabelsTransform,
    width = '100%',
    wrapperStyles,
  } = appearance;
  const updatedMargin = { ...defaultMargin, ...margin };

  const { activeLegends, colors, emptyStateConfig, labels, setPrimaryLegendsList, strokeColors } = useChartContext();
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isTooltipActive, setIsTooltipActive] = useState<boolean>(false);

  const uniqueXAxisDataSeries = useMemo(() => {
    const groups = data?.groups ?? [];
    const series = groups.flatMap((group) => (group.values ?? []).map((value) => value.x));
    return Array.from(new Set(series));
  }, [data?.groups]);

  const chartData: Record<string, Value[]> = useMemo(() => {
    if (!data?.groups?.length) {
      return {};
    }

    return data.groups.reduce(
      (acc, { name, values }) => ({ ...acc, [name]: values.map((dataKeys) => ({ ...dataKeys, name })) }),
      {}
    );
  }, [data.groups]);

  const handleMouseLeave = useCallback(() => {
    if (!hideTooltip) {
      setIsTooltipActive(false);
    }
  }, [hideTooltip]);

  const showEmptyState = useMemo(() => {
    return showEmptyStateForScatterChart({
      ...emptyStateConfig,
      data,
    });
  }, [data, emptyStateConfig]);

  const { domain, xAxisTicks } = useMemo(() => {
    if (!uniqueXAxisDataSeries.length) {
      return {
        domain: undefined,
        xAxisTicks: undefined,
      };
    }

    let min = Infinity;
    let max = -Infinity;

    for (const x of uniqueXAxisDataSeries) {
      if (x < min) min = x;
      if (x > max) max = x;
    }

    if (min === Infinity || max === -Infinity) {
      return {
        domain: undefined,
        xAxisTicks: undefined,
      };
    }

    const padding = (max - min) * 0.05;
    const domain = [min - padding, max + padding];

    const xAxisTicks = [];
    const rangeMin = min;
    const rangeMax = max;
    const interval = (rangeMax - rangeMin) / 4;

    for (let i = 0; i <= 4; i++) {
      xAxisTicks.push(Math.round(rangeMin + i * interval));
    }

    return { domain, xAxisTicks };
  }, [uniqueXAxisDataSeries]);

  const { minY, maxY } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;

    data?.groups?.forEach((group) => {
      group.values?.forEach((value) => {
        if (value.y !== undefined) {
          if (value.y < min) min = value.y;
          if (value.y > max) max = value.y;
        }
      });
    });

    if (min === Infinity) {
      return {
        minY: 0,
        maxY: 1,
      };
    }

    if (min === max) {
      return {
        minY: min,
        maxY: min + 1,
      };
    }

    return {
      minY: min,
      maxY: max,
    };
  }, [data?.groups]);

  useEffect(() => {
    setPrimaryLegendsList(data.groups.map(({ name }) => name) ?? []);
  }, [data.groups]);

  return (
    <div css={[styles.mainWrapper, wrapperStyles]}>
      <div
        className='pdf-chart-view'
        style={{
          height,
          minHeight: showEmptyState ? EMPTY_STATE_MIN_HEIGHT : height,
          width,
        }}
      >
        {!showEmptyState ? (
          <ResponsiveContainer width='100%' height='100%' key={JSON.stringify(data.groups || {})}>
            <RechartScatterChart
              margin={updatedMargin}
              onMouseLeave={handleMouseLeave}
              onMouseMove={(_, event) => {
                if (!hideTooltip) {
                  setTooltipPosition({
                    x: event.clientX,
                    y: event.clientY,
                  });
                }
              }}
            >
              {showGridLines && <CartesianGrid stroke={theme.colors.neutral10} strokeDasharray='1 1' />}

              <XAxis
                axisLine={axisLine}
                dataKey='x'
                domain={domain}
                dy={8}
                fontSize={14}
                hide={!showXAxis}
                interval='equidistantPreserveStart'
                tick={
                  customXAxisTick
                    ? (props) => {
                        return customXAxisTick?.({
                          groupName: props.payload.value,
                          labels,
                          x: props.x,
                          y: props.y,
                        });
                      }
                    : undefined
                }
                tickFormatter={customXAxisTickFormat}
                tickLine={false}
                ticks={xAxisTicks}
                type='number'
              />

              <YAxis
                axisLine={axisLine}
                dataKey='y'
                fontSize={14}
                hide={!showYAxis}
                tickFormatter={customYAxisTickFormat}
                tick={
                  customYAxisTick
                    ? (props) => {
                        return customYAxisTick?.({
                          groupName: props.payload.value,
                          labels,
                          x: props.x,
                          y: props.y,
                        });
                      }
                    : undefined
                }
                tickLine={false}
                type='number'
              />

              {activeLegends.map((legend) => {
                const color = colors[legend] || theme.colors.neutral50;
                return (
                  <Scatter
                    data={chartData?.[legend]}
                    key={legend}
                    name={legend}
                    onMouseLeave={handleMouseLeave}
                    onMouseMove={() => {
                      if (!hideTooltip) {
                        setIsTooltipActive(true);
                      }
                    }}
                    shape={(props: ScatterPointItem) => {
                      const pointData = props?.payload ?? { y: 0 };

                      return (
                        <circle
                          {...props}
                          fill={color}
                          r={calcPlotRadius({
                            fixSize,
                            maxRadius,
                            maxY,
                            minRadius,
                            minY,
                            value: pointData.y,
                          })}
                          stroke={strokeColors?.[legend]}
                          strokeWidth={2}
                        />
                      );
                    }}
                  />
                );
              })}

              {renderHorizontalMarker(data.markers, colors, hideMarkerEnds)}

              <RechartTooltip
                active={isTooltipActive}
                content={(props) => {
                  const { active, payload } = props;

                  if (!payload?.length || !active || hideTooltip) {
                    return null;
                  }

                  const { x = 0, y = 0, name } = payload?.[0]?.payload || {};

                  return (
                    <Tooltip
                      data={[
                        {
                          color: colors[name],
                          formattedValue: customYAxisTickFormat?.(y) || defaultFormatter(y),
                          id: 'y',
                          label: labels[y] || labels[name],
                          groupId: name,
                        },
                      ]}
                      labelsTransform={tooltipLabelsTransform}
                      name={
                        customTooltipTitle?.({
                          groupName: name,
                        }) || customXAxisTickFormat?.(x, 0)
                      }
                      skipActiveLegendsCheck
                      xPos={tooltipPosition.x}
                      yPos={tooltipPosition.y}
                    />
                  );
                }}
                cursor={
                  hideHoveringCursor
                    ? false
                    : {
                        fill: theme.colors.neutral20,
                        strokeDasharray: '10 3',
                        strokeWidth: 1,
                      }
                }
              />
            </RechartScatterChart>
          </ResponsiveContainer>
        ) : (
          <EmptyData emptyStateConfig={emptyStateConfig} />
        )}
      </div>
    </div>
  );
});

ScatterChart.displayName = 'ScatterChart';

const styles = {
  mainWrapper: css`
    overflow: auto;
    position: relative;
  `,
};
